import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { authApi } from '../api/client';

interface User {
  id: string;
  email: string;
  displayName: string;
  role: 'user' | 'admin' | 'superadmin';
  status: 'trial' | 'active' | 'suspended' | 'deleted';
  emailVerified: boolean;
  trialEndsAt?: string;
  createdAt: string;
}

interface AuthState {
  user: User | null;
  accessToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  
  // Actions
  login: (email: string, password: string) => Promise<boolean>;
  register: (email: string, password: string, displayName?: string) => Promise<boolean>;
  logout: () => Promise<void>;
  refreshToken: () => Promise<boolean>;
  setUser: (user: User) => void;
  setError: (error: string | null) => void;
  checkAuth: () => Promise<void>;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      accessToken: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
      
      login: async (email, password) => {
        set({ isLoading: true, error: null });
        try {
          const response = await authApi.login({ email, password });
          set({
            user: response.user as User,
            accessToken: response.accessToken,
            isAuthenticated: true,
            isLoading: false,
          });
          return true;
        } catch (err: any) {
          const message = err.response?.json 
            ? (await err.response.json()).message 
            : 'Login fehlgeschlagen';
          set({ error: message, isLoading: false });
          return false;
        }
      },
      
      register: async (email, password, displayName) => {
        set({ isLoading: true, error: null });
        try {
          const response = await authApi.register({ email, password, displayName });
          set({
            user: response.user as User,
            accessToken: response.accessToken,
            isAuthenticated: true,
            isLoading: false,
          });
          return true;
        } catch (err: any) {
          const message = err.response?.json 
            ? (await err.response.json()).message 
            : 'Registrierung fehlgeschlagen';
          set({ error: message, isLoading: false });
          return false;
        }
      },
      
      logout: async () => {
        try {
          await authApi.logout();
        } catch (err) {
          // Ignore errors
        }
        set({
          user: null,
          accessToken: null,
          isAuthenticated: false,
        });
      },
      
      refreshToken: async () => {
        try {
          const response = await authApi.refresh();
          set({
            user: response.user as User,
            accessToken: response.accessToken,
            isAuthenticated: true,
          });
          return true;
        } catch (err) {
          set({
            user: null,
            accessToken: null,
            isAuthenticated: false,
          });
          return false;
        }
      },
      
      setUser: (user) => set({ user }),
      
      setError: (error) => set({ error }),
      
      checkAuth: async () => {
        const { accessToken } = get();
        if (!accessToken) {
          set({ isAuthenticated: false });
          return;
        }
        
        try {
          const response = await authApi.me();
          set({ user: response.user as User, isAuthenticated: true });
        } catch (err) {
          // Try to refresh
          const refreshed = await get().refreshToken();
          if (!refreshed) {
            set({
              user: null,
              accessToken: null,
              isAuthenticated: false,
            });
          }
        }
      },
    }),
    {
      name: 'kolumi-auth',
      partialize: (state) => ({
        accessToken: state.accessToken,
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);
